/**
 * Shared card wrapper for frontend tool UI components.
 *
 * Provides consistent chrome: accent-colored border, badge label,
 * loading spinner, and "Response submitted" footer. Each tool's render
 * component passes its interactive content as children.
 */
import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "~/lib/utils";

const ACCENT_STYLES = {
  blue: {
    border: "border-blue-200",
    badgeBg: "bg-blue-50",
    badgeText: "text-blue-600",
  },
  amber: {
    border: "border-amber-200",
    badgeBg: "bg-amber-50",
    badgeText: "text-amber-600",
  },
  green: {
    border: "border-green-200",
    badgeBg: "bg-green-50",
    badgeText: "text-green-600",
  },
  indigo: {
    border: "border-indigo-200",
    badgeBg: "bg-indigo-50",
    badgeText: "text-indigo-600",
  },
  rose: {
    border: "border-rose-200",
    badgeBg: "bg-rose-50",
    badgeText: "text-rose-600",
  },
} as const;

type AccentColor = keyof typeof ACCENT_STYLES;

interface ToolCardProps {
  label: string;
  color: AccentColor;
  isLoading: boolean;
  loadingText?: string;
  isSubmitted: boolean;
  children: ReactNode;
}

export function ToolCard({
  label,
  color,
  isLoading,
  loadingText,
  isSubmitted,
  children,
}: ToolCardProps) {
  const accent = ACCENT_STYLES[color];

  return (
    <div className={cn("border-2 rounded-lg p-4 my-2 space-y-3", accent.border)}>
      <div className="flex items-center gap-2 mb-1">
        <span
          className={cn(
            "text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded",
            accent.badgeBg,
            accent.badgeText
          )}
        >
          {label}
        </span>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          <span>{loadingText}</span>
        </div>
      ) : (
        children
      )}

      {isSubmitted && <p className="text-xs text-muted-foreground">Response submitted</p>}
    </div>
  );
}
